import { flightsClearConfirmation, flightsClearOptionsIds, flightsClearPagination, flightsFilterClear, flightsSearchRequest, setAirSearchType } from 'action-creators/flights';
import { accountFailure, accountRequest } from 'action-creators/user';
import { flightResults, flightResultsBySector, getFilters, getFiltersBySector, searchFlights, searchFlightsBySector, selectFlightResultsBySector } from 'actions/flights';
import { updateAccount } from 'actions/user';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { FlightFilterResponse } from 'models/flightFilter';
import { FlightResultRequestOrderCriteriaEnum, FlightStretch, SearchType } from 'models/flightSearch';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FlightsList from 'screens/components/FlightsList';
import FlightsListBySector from 'screens/components/FlightsListBySector';
import { getAirSearchType } from 'services/flightService';
import { fetchAccount } from 'services/userService';
import { MyThunkDispatch, RootState } from 'store';
import { extractErrorMessage } from 'utils/generic';
const FlightsListContainerV2 = () => {
    const history = useHistory();
    const dispatch = useDispatch<MyThunkDispatch>();
    const {
        departurePoint,
        arrivalPoint,
        departureDate,
        returnDate,
        search,
        cabin,
        baggageIncluded,
        flightTabFilter,
        orderCriteria,
        isLoadingResults,
        travelers,
        departureTime,
        returnTime,
        directFlight,
        branchCompany,
        multiDestinationTrips,
        flightStretch,
        airSearchType
    } = useSelector((state: RootState) => state.flights);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [availableFilters, setAvailableFilters] = useState<FlightFilterResponse>();
    const { t } = useStoreTranslation();
    const toast = useToast();
    const user = useSelector((state: RootState) => state.user.accountResult);
    const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration);
    const [dataFlight, setDataFlight] = useState([]);
    const [selectedFlight, setDataSelectedFlight] = useState([]);
    const isMobile = useIsMobile();
    const [initialDataFetched, setInitialDataFetched] = useState(false);

    // Memoización de search y availableFilters
    const memoizedSearch = useMemo(() => search, [search?.resultsAvailableUntil, search?.results, search?.currency]);
    const memoizedAvailableFilters = useMemo(() => availableFilters, [availableFilters]);

    
    // Determina el tipo de búsqueda (por sector o agrupada)
    const getSearchType = useMemo(() => {
        if (flightStretch === FlightStretch.ONE_WAY) return SearchType.BY_GROUPED_RESULT;
        if (flightStretch === FlightStretch.ROUND_TRIP || flightStretch === FlightStretch.MULTIDESTINATION) {
            if (airSearchType && ((airSearchType.isDomestic && companyConfiguration.configuration.nationalAirBySector) ||
                (airSearchType.isInternational && companyConfiguration.configuration.internationalAirBySector))) {
                return SearchType.BY_SECTOR;
            }
            return SearchType.BY_GROUPED_RESULT;
        }
        return SearchType.BY_GROUPED_RESULT;
    }, [flightStretch, airSearchType, companyConfiguration]);

    // Realiza la búsqueda de vuelos y maneja los resultados
    const handleFlightSearch = useCallback(async () => {
        if (flightStretch !== FlightStretch.MULTIDESTINATION && !departurePoint && !arrivalPoint && !departureDate && !cabin) return;
        setLoadingSearch(true);
        try {
            dispatch(accountRequest());
            const { data: { data } } = await fetchAccount();
            if (!data.sessionToken || data.isOperator) {
                dispatch(accountFailure('sessionToken is invalid'));
                history.replace('/login');
                return;
            }
            updateAccount(dispatch, { ...data, companyId: user.companyId, companyName: user.companyName });
            const payload = { baggageIncluded, departurePoint, arrivalPoint, departureDate, cabin, returnDate: !(departurePoint && !returnDate) ? returnDate : undefined, newSessionToken: data.sessionToken, travelers, departureTime, returnTime, directFlight, branchCompany, multiDestinationTrips, flightStretch, };
            dispatch(flightsFilterClear());
            dispatch(flightsSearchRequest(payload));
            dispatch(flightsClearPagination());
            dispatch(flightsClearConfirmation());
            dispatch(flightsClearOptionsIds());

            let resolvedSearchType = getSearchType;
            if (!airSearchType && flightStretch !== FlightStretch.ONE_WAY && departurePoint?.code && arrivalPoint?.code) {
                const fetchedAirSearchType = await getAirSearchType(departurePoint.code, arrivalPoint.code);
                dispatch(setAirSearchType(fetchedAirSearchType));
                if (flightStretch === FlightStretch.ROUND_TRIP || flightStretch === FlightStretch.MULTIDESTINATION) {
                    if ((fetchedAirSearchType.isDomestic && companyConfiguration.configuration?.nationalAirBySector) ||
                        (fetchedAirSearchType.isInternational && companyConfiguration.configuration?.internationalAirBySector)) {
                        resolvedSearchType = SearchType.BY_SECTOR;
                    }
                }
            }

            const searchResults: any = await (resolvedSearchType === SearchType.BY_SECTOR ? dispatch(searchFlightsBySector(payload)) : dispatch(searchFlights(payload)));
            if (searchResults.results) {
                const results = resolvedSearchType === SearchType.BY_SECTOR ? searchResults.results.airResults : searchResults.results.results;
                setDataFlight(results);
            }

            if (searchResults?.resultsCount === 0) {
                history.replace('/corporate/booking/flights', { showNoResults: true });
                return;
            }
            
            return searchResults;
        } catch (error) {
            const message = extractErrorMessage(error);
            if (message?.startsWith('Currency reported is different')) {
                toast.add({ type: 'danger', message: t('flightsList.error.differentCurrency') });
                history.replace('/corporate/booking/flights');
            } else if (message?.toLowerCase().includes('cannot search for flights departing before')) {
                history.replace('/corporate/booking/flights', { showNoResults: true });
            } else {
                toast.add({ type: 'danger', message: message || t('defaultError.default') });
                history.replace('/corporate/booking/flights', { showNoResults: true });
            }
        } finally {
            setLoadingSearch(false);
        }
    }, [arrivalPoint, baggageIncluded, cabin, departureDate, departurePoint, departureTime, dispatch, returnDate, returnTime, travelers, directFlight, branchCompany, multiDestinationTrips, flightStretch, getSearchType, airSearchType, companyConfiguration, history, t, toast, user.companyId, user.companyName]);

    // Obtiene los filtros disponibles para la búsqueda
    const handleFetchFilters = useCallback(async () => {
        try {
            setLoadingSearch(true);
            const filters = getSearchType === SearchType.BY_SECTOR ? await dispatch(getFiltersBySector()) : await dispatch(getFilters());
            setAvailableFilters(filters);
        } catch (error) {
            toast.add({ type: 'danger', message: t('filters.fetchError') });
        } finally {
            setLoadingSearch(false);
        }
    }, [dispatch, t, toast, getSearchType]);


    // Obtiene los resultados de la búsqueda (aplicando orden y filtros)
    const handleFetchResults = useCallback(async () => {
        try {
            setLoadingSearch(true);
            await (getSearchType === SearchType.BY_SECTOR ? dispatch(flightResultsBySector(orderCriteria)) : dispatch(flightResults(orderCriteria)));
        } finally {
            setLoadingSearch(false);
        }
    }, [dispatch, orderCriteria, getSearchType]);

     // Maneja la solicitud de ordenamiento de resultados (búsqueda agrupada)
     const handleRequest = useCallback(
        (value?: FlightResultRequestOrderCriteriaEnum) => getSearchType === SearchType.BY_GROUPED_RESULT ? dispatch(flightResults(value || orderCriteria)) : null,
        [dispatch, orderCriteria, getSearchType],
    );

    // Maneja la solicitud de ordenamiento de resultados (búsqueda por sector)
    const handleRequestBySector = useCallback(
        (value?: FlightResultRequestOrderCriteriaEnum) => {
            if (getSearchType === SearchType.BY_SECTOR) {
                dispatch(flightResultsBySector(value || orderCriteria));
            }
        },
        [dispatch, orderCriteria, getSearchType],
    );

    // Maneja la selección de un vuelo (búsqueda por sector)
    const handleRequestSelectFlight = useCallback(
        async (value?: FlightResultRequestOrderCriteriaEnum) => {
            if (getSearchType === SearchType.BY_SECTOR) {
                setLoadingSearch(true);
                await dispatch(selectFlightResultsBySector(value || orderCriteria));
                await handleFetchFilters();
                setLoadingSearch(false);
            }
        },
        [dispatch, orderCriteria, getSearchType, handleFetchFilters],
    );

    useEffect(() => {
        const fetchInitialData = async () => {
            const now = new Date();
            const before = memoizedSearch?.resultsAvailableUntil ? new Date(memoizedSearch.resultsAvailableUntil) : null;
            const hasExpired = before ? before < now : true;
            if ((hasExpired || !memoizedSearch?.results?.length) && !isLoadingResults && !loadingSearch) {
                await handleFlightSearch();
                await handleFetchResults();
                await handleFetchFilters();
            }
            if (!memoizedAvailableFilters && !availableFilters) {
                await handleFetchFilters();
            }
            
        };
        if(JSON.parse(sessionStorage.getItem('reloadSearch'))){
          setInitialDataFetched(false);
          sessionStorage.removeItem('reloadSearch')
        }
        if (((departurePoint && arrivalPoint && departureDate) || (multiDestinationTrips && multiDestinationTrips.length > 1))&& 
            cabin && !initialDataFetched) {
   
            fetchInitialData();
            setInitialDataFetched(true);
        }
    }, [departurePoint, arrivalPoint, departureDate, cabin, memoizedSearch, isLoadingResults, loadingSearch, memoizedAvailableFilters, handleFlightSearch, handleFetchFilters, handleFetchResults, initialDataFetched]);

    const roundTrip = useMemo(() => !(departurePoint && !returnDate), [departurePoint, returnDate]);

        return (
            <>
                {getSearchType === SearchType.BY_SECTOR ? (
                    <FlightsListBySector
                        isLoading={loadingSearch}
                        isLoadingResults={isLoadingResults}
                        departurePoint={departurePoint}
                        arrivalPoint={arrivalPoint}
                        departureDate={departureDate}
                        returnDate={returnDate}
                        orderCriteria={orderCriteria}
                        flightsResult={search?.results || []}
                        onRequest={handleRequestBySector}
                        flightTabFilter={flightTabFilter}
                        currency={search?.currency || 'COP'}
                        availableFilters={availableFilters}
                        roundTrip={roundTrip}
                        cabin={cabin}
                        baggageIncluded={baggageIncluded}
                        travelers={travelers}
                        directFlight={directFlight}
                        flightStretch={flightStretch}
                        multiDestinationTrips={multiDestinationTrips}
                        onRequestSelectFlight={handleRequestSelectFlight}
                    />
                ) : (
                    <FlightsList
                        isLoading={loadingSearch}
                        isLoadingResults={isLoadingResults}
                        departurePoint={departurePoint}
                        arrivalPoint={arrivalPoint}
                        departureDate={departureDate}
                        returnDate={returnDate}
                        orderCriteria={orderCriteria}
                        flightsResult={search?.results || []}
                        onRequest={handleRequest}
                        flightTabFilter={flightTabFilter}
                        currency={search?.currency || 'COP'}
                        availableFilters={availableFilters}
                        roundTrip={roundTrip}
                        cabin={cabin}
                        baggageIncluded={baggageIncluded}
                        travelers={travelers}
                        directFlight={directFlight}
                        flightStretch={flightStretch}
                        multiDestinationTrips={multiDestinationTrips}
                    />
                )}
            </>
        );
    };
    
    export default FlightsListContainerV2;
